import { yupResolver } from '@hookform/resolvers/yup'
import { Add } from '@mui/icons-material'
import { Button, Stack, Typography } from '@mui/material'
import { getApiClient } from '@src/services/api-client'
import { nanoid } from 'nanoid'
import { memo, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ActionsDialog } from '../../../components'
import allApis from '../../../data/api'
import {
  AdditionnalBankruptcies,
  Applicant,
  ApplicantBankruptcies,
  CreditApplication,
  applicantBankruptciesSchema,
} from '../../../data/types'
import { CreditReport } from '../../../data/types/CreditReportSchema'
import { reportErrorToConsole } from '../../../services/error-logger'
import BankruptcyComponent from './Bankruptcy'
import ReportedBankruptcy from './ReportedBankruptcy'

interface Props {
  open: boolean
  creditApplication: CreditApplication
  applicant: Applicant | undefined
  onConfirm: (data: ApplicantBankruptcies) => void
  onCancel: () => void
  disableConfirm?: boolean
}

const apiClient = getApiClient()

const EditBankruptciesDialog = ({ applicant, creditApplication, onConfirm, onCancel, open, disableConfirm }: Props) => {
  const { t } = useTranslation()
  const [currentApplicantCreditReport, setCurrentApplicantCreditReport] = useState<CreditReport | null>()
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: applicant,
    // Forcing type to be backward compatible with the old schema (should be removed in the future)
    resolver: yupResolver(applicantBankruptciesSchema),
  })

  const {
    fields: currentAdditionnalBankruptciesFields,
    append: additionnalBankruptciesAppend,
    remove: additionnalBankruptciesRemove,
  } = useFieldArray({
    name: 'additionnalBankruptcies',
    control,
  })

  useEffect(() => {
    reset(applicant)
    if (applicant?.hardHitReport?.creditReportId && apiClient) {
      allApis.credit
        .getCreditReportByCreditReportId(apiClient, {
          financingProgramId: creditApplication.financingProgramId,
          creditApplicationId: creditApplication.id,
          creditReportId: applicant.hardHitReport.creditReportId,
        })
        .then((applicantReport) => setCurrentApplicantCreditReport(applicantReport.creditReport))
        .catch(reportErrorToConsole)
    }
  }, [
    applicant,
    reset,
    creditApplication.id,
    creditApplication.financingProgramId,
    applicant?.hardHitReport?.creditReportId,
  ])

  return (
    <ActionsDialog
      disabled={disableConfirm}
      title={
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={3}>
          <Typography>{t('creditReport.bankruptcyHistory')}</Typography>
          <Button
            variant="contained"
            color="success"
            endIcon={<Add />}
            onClick={() => additionnalBankruptciesAppend({ id: nanoid() } as AdditionnalBankruptcies)}
          >
            {t('common.add')}
          </Button>
        </Stack>
      }
      open={open}
      onCancel={() => {
        reset(applicant)
        onCancel()
      }}
      onConfirm={handleSubmit(onConfirm, reportErrorToConsole)}
    >
      {currentApplicantCreditReport?.bankruptcies?.map((bankruptcy) => (
        <ReportedBankruptcy bankruptcy={bankruptcy} key={bankruptcy.caseNumber} />
      ))}
      {currentAdditionnalBankruptciesFields.map((bankruptcy, index) => (
        <BankruptcyComponent
          index={index}
          register={register}
          error={errors?.additionnalBankruptcies}
          control={control}
          key={bankruptcy.id}
          isSyndicReported
          onDeleteClicked={() => additionnalBankruptciesRemove(index)}
          fieldName="additionnalBankruptcies"
        />
      ))}
    </ActionsDialog>
  )
}

export default memo(EditBankruptciesDialog)
